import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductData } from '../products.interface';
import { OptionItem } from '../../../shared/types/api-responses.type';
import { MasterProductFormValidations } from './product-form-validations';
import { ProductsService } from '../../../services/products/products.service';
import { firstValueFrom } from 'rxjs';
import { showAlert } from '../../../shared/helpers/alert';
import { CategoriesService } from '../../../services/categories/categories.service';
import { MasterMerchantProductFormValidations } from '../../merchantProducts/merchantProduct-form/merchantProduct-form-validations';
import { ProvidersService } from '../../../services/providers/providers.service';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.css'],
})
export class ProductFormComponent implements OnInit {
  loading: boolean = false;
  form!: FormGroup;
  readonly id: string;
  productData!: ProductData;
  categories: OptionItem[] = [];
  providers: OptionItem[] = [];
  activeStatus: OptionItem[] = [];
  isChangePassword = false;
  readonly MasterProductFormValidations = MasterProductFormValidations;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly productService: ProductsService,
    private readonly categoryService: CategoriesService,
    private readonly providerService: ProvidersService,
    private readonly router: Router
  ) {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
  }

  async ngOnInit(): Promise<void> {
    this.activeStatus = [
      { label: 'Active', value: '1' },
      { label: 'Inactive', value: '0' },
    ];
    this.initForm();
    await this.loadInitialData();
  }

  private initForm(): void {
    this.form = this.fb.group({
      id: [''],
      category_code: [null, Validators.required],
      name: [''],
      brand: [''],
      price: [0],
      selling_price: [0],
      code: [''],
      provider_code: [null, Validators.required],
      provider_product: [''],
      admin_fee: ['0'], // Default to '0' if not provided
      active: [''],
    });

    this.form.valueChanges.subscribe((value) => console.log('Form Value Changed:', value));
  }

  private async loadInitialData(): Promise<void> {
    this.loading = true;
    try {
      await this.getCategoryData();
      await this.getProviderData();

      if (this.id) {
        await this.getProductData();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showNotification('error', 'Error', 'Failed to load some initial data.');
    } finally {
      this.loading = false;
    }
  }

  private async getCategoryData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.categoryService.getCategories());
      this.categories = data.map((category) => ({ label: category.name, value: category.code }));
      console.log('categoryOption: ', this.categories);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch category', JSON.stringify(error));
    }
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.code }));
      console.log('providerOption: ', this.providers);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  private async getProductData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.productService.getProductById(this.id));
      this.productData = data;
      this.patchProductData(data);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch product', JSON.stringify(error));
    }
  }

  private patchProductData(product: ProductData): void {
    console.log('Patching Product Data:', product);
    console.log('Form Controls Before Patch:', this.form.controls);

    this.form.patchValue({
      id: product.id || '',
      category_code: product.category_code || '',
      name: product.name || '',
      brand: product.brand || '',
      price: product.price || '0',
      selling_price: product.selling_price || '0',
      code: product.code || '',
      provider_code: product.provider_code || '',
      provider_product: product.provider_product || '',
      admin_fee: product.admin_fee || '0', // Default to '0' if not provided
      active: product.active || '',
    });

    console.log('Form Value After Patch:', this.form.value);
  }

  async saveData(): Promise<void> {
    console.log('Form value before saving:', this.form.value);

    if (this.id) {
      await this.updateProduct();
      return;
    }
    await this.createProduct();
  }

  async createProduct(): Promise<void> {
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.productService.createProduct(this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'Product created successfully');
        await this.router.navigate(['/products/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to create product');
      }
    } catch (error) {
      this.showNotification('error', 'Error', JSON.stringify(error));
    } finally {
      this.loading = false;
    }
  }

  async updateProduct(): Promise<void> {
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.productService.updateProduct(this.id, this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'Product updated successfully');
        await this.router.navigate(['/products/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to update product');
      }
    } catch (error: any) {
      console.log('Error:', error);
      this.showNotification('error', 'Error', error);
    } finally {
      this.loading = false;
    }
  }

  private showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
    showAlert({ type, title, text }).then();
  }

  protected readonly MasterMerchantProductFormValidations = MasterMerchantProductFormValidations;
}
